import { ChangeEvent, useMemo, useState } from 'react'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Checkbox from '@mui/material/Checkbox'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Control, UseFormSetValue, useWatch, UseFormGetValues } from 'react-hook-form'
import { IPermission } from '../../../../compiler/global.interface'
import { CheckBoxInput } from '../../../global/Input'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import {
  PermissionAction,
  PermissionInheritanceMap,
} from './User/permissionInheritance'
import {
  bulkPermissionChanges,
  isBulkPermissionChecked,
  isExplicitDashboardPermission,
  permissionRowChanges,
  PermissionTarget,
} from './permissionEditorChanges'

// Interface สำหรับ hierarchical structure
export interface IPermissionNode {
  permission: IPermission
  originalIndex: number
  children: IPermissionNode[]
}

// Helper function เพื่อแปลง flat array เป็น tree structure
export function buildPermissionTree(permissions: IPermission[]): IPermissionNode[] {
  const tree: IPermissionNode[] = []
  const indexMap = new Map<string, number>()

  // สร้าง index map สำหรับหา original index
  permissions.forEach((p, index) => {
    indexMap.set(p.roles_code, index)
  })

  // แยก root และ children
  const rootItems: IPermission[] = []
  const childrenMap = new Map<string, IPermission[]>()

  permissions.forEach((permission) => {
    const code = permission.roles_code
    if (!code.includes('-')) {
      // Root item (e.g., "02", "03", "04")
      rootItems.push(permission)
    } else {
      // Child item (e.g., "02-01", "02-02")
      const parentCode = code.split('-')[0]
      if (!childrenMap.has(parentCode)) {
        childrenMap.set(parentCode, [])
      }
      childrenMap.get(parentCode)!.push(permission)
    }
  })

  // สร้าง tree structure
  rootItems.forEach((root) => {
    const children = childrenMap.get(root.roles_code) || []
    const childNodes: IPermissionNode[] = children
      .sort((a, b) => a.roles_code.localeCompare(b.roles_code))
      .map((child) => ({
        permission: child,
        originalIndex: indexMap.get(child.roles_code)!,
        children: [],
      }))

    tree.push({
      permission: root,
      originalIndex: indexMap.get(root.roles_code)!,
      children: childNodes,
    })
  })

  // Sort by roles_code
  return tree.sort((a, b) => {
    const aNum = parseInt(a.permission.roles_code) || 0
    const bNum = parseInt(b.permission.roles_code) || 0
    return aNum - bNum
  })
}

interface IHierarchicalPermissionTableProps {
  control: Control<{ permissionData: IPermission[] }>
  setValue: UseFormSetValue<{ permissionData: IPermission[] }>
  getValues: UseFormGetValues<{ permissionData: IPermission[] }>
  permissionFields: IPermission[]
  replaceFields?: (value: IPermission[]) => void
  inheritedPermissions?: PermissionInheritanceMap
  isInheritedLoading?: boolean
}

export default function HierarchicalPermissionTable({
  control,
  setValue,
  getValues,
  permissionFields,
  replaceFields,
  inheritedPermissions = {},
  isInheritedLoading = false,
}: IHierarchicalPermissionTableProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const permissionTree = useMemo(() => {
    return buildPermissionTree(permissionFields)
  }, [permissionFields])

  const toggleExpand = (code: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(code)) {
        next.delete(code)
      } else {
        next.add(code)
      }
      return next
    })
  }

  const expandAll = () => {
    const allCodes = permissionTree
      .filter((node) => node.children.length > 0)
      .map((node) => node.permission.roles_code)
    setExpandedItems(new Set(allCodes))
  }

  const collapseAll = () => {
    setExpandedItems(new Set())
  }

  return (
    <Grid container sx={{ height: '100%', overflow: 'auto' }}>
      {permissionFields.some(permission => isExplicitDashboardPermission(permission.roles_code)) && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 1.5 }}>
            สิทธิ์ “ดู Dashboard และข้อมูลยอดขาย” ใช้เฉพาะ Read และต้องกำหนดแยกที่รายการนี้
            การเลือกหรือยกเลิกทั้งหมด รวมถึงรายการ Welcome จะไม่เปลี่ยนสิทธิ์นี้
          </Typography>
        </Grid>
      )}
      {/* Left Column - Menu Names */}
      <Grid item xs={5} sx={{ height: '100%' }}>
        <Box
          className="row"
          px={3}
          py={2}
          borderBottom={'1px solid'}
          borderColor={'divider'}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="headline1" lineHeight={'80%'} color="primary.dark">
            รายการ
          </Typography>
          {isInheritedLoading && <CircularProgress size={16} />}
          <Stack direction="row" spacing={1}>
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
              onClick={expandAll}
            >
              ขยายทั้งหมด
            </Typography>
            <Typography variant="caption" color="text.secondary">
              |
            </Typography>
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
              onClick={collapseAll}
            >
              ยุบทั้งหมด
            </Typography>
          </Stack>
        </Box>
        {/* Select All Row */}
        <Box
          height={'48px'}
          className="row"
          px={3}
          py={2}
          sx={{ 
            borderRight: '1px solid', 
            borderBottom: '2px solid',
            borderColor: 'divider',
            borderBottomColor: 'primary.main',
            bgcolor: 'primary.100', 
            color: 'primary.dark',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Checkbox
            size="small"
            indeterminate
            checked={false}
            sx={{ p: 0, color: 'primary.main', pointerEvents: 'none' }}
          />
          <Typography fontWeight={700} color="primary.dark">เลือกทั้งหมด</Typography>
        </Box>
        {/* Permission Tree */}
        {permissionTree.map((node, index) => (
          <PermissionMenuLabel
            key={node.permission.roles_code}
            node={node}
            index={index}
            expandedItems={expandedItems}
            toggleExpand={toggleExpand}
          />
        ))}
      </Grid>

      {/* Right Column - Checkboxes */}
      <Grid item xs={7} sx={{ height: '100%' }}>
        {/* Header */}
        <Stack direction={'row'} borderBottom={'1px solid'} borderColor={'divider'}>
          <Box flex={1} px={3} py={2}>
            <Typography variant="headline2" color="primary.dark">
              Read
            </Typography>
          </Box>
          <Box flex={1} px={3} py={2}>
            <Typography variant="headline2" color="primary.dark">
              Add
            </Typography>
          </Box>
          <Box flex={1} px={3} py={2}>
            <Typography variant="headline2" color="primary.dark">
              Edit
            </Typography>
          </Box>
          <Box flex={1} px={3} py={2}>
            <Typography variant="headline2" color="primary.dark">
              Delete
            </Typography>
          </Box>
          <Box flex={1} px={3} py={2}>
            <Typography variant="headline2" color="primary.dark">
              Approval
            </Typography>
          </Box>
        </Stack>
        {/* Select All Checkboxes */}
        <PermissionFieldHeader control={control} setValue={setValue} getValues={getValues} replaceFields={replaceFields} />
        {/* Permission Tree Checkboxes */}
        {permissionTree.map((node, index) => (
          <PermissionMenuCheckboxes
            key={node.permission.roles_code}
            node={node}
            index={index}
            expandedItems={expandedItems}
            control={control}
            setValue={setValue}
            inheritedPermissions={inheritedPermissions}
          />
        ))}
      </Grid>
    </Grid>
  )
}

// Component สำหรับแสดง label ของ menu (ซ้ายมือ)
function PermissionMenuLabel({
  node,
  index,
  expandedItems,
  toggleExpand,
}: {
  node: IPermissionNode
  index: number
  expandedItems: Set<string>
  toggleExpand: (code: string) => void
}) {
  const hasChildren = node.children.length > 0
  const isExpanded = expandedItems.has(node.permission.roles_code)

  return (
    <>
      {/* Root Item */}
      <Box
        height={'48px'}
        className="row"
        px={hasChildren ? 1 : 3}
        py={2}
        sx={{
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: hasChildren ? 'common.white' : (index % 2 === 0 ? 'grey.50' : 'common.white'),
          display: 'flex',
          alignItems: 'center',
          borderBottom: hasChildren ? '1px solid' : 'none',
          borderBottomColor: 'divider',
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bgcolor: hasChildren ? 'grey.50' : 'action.hover',
          },
        }}
      >
        {hasChildren && (
          <IconButton 
            size="small" 
            onClick={() => toggleExpand(node.permission.roles_code)} 
            sx={{ 
              mr: 0.5,
              color: 'primary.main',
              bgcolor: isExpanded ? 'primary.50' : 'transparent',
              '&:hover': { bgcolor: 'primary.100' },
            }}
          >
            {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        )}
        {!hasChildren && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.300', mr: 1.5, ml: 1 }} />}
        <Typography 
          fontWeight={hasChildren ? 600 : 400} 
          color={hasChildren ? 'primary.dark' : 'text.primary'}
          fontSize={hasChildren ? '0.95rem' : '0.875rem'}
        >
          {node.permission.roles_name}
        </Typography>
        {hasChildren && (
          <Typography 
            variant="caption" 
            sx={{ 
              ml: 1, 
              px: 1, 
              py: 0.25, 
              bgcolor: 'primary.50', 
              color: 'primary.main', 
              borderRadius: 1,
              fontWeight: 500,
            }}
          >
            {node.children.length} รายการย่อย
          </Typography>
        )}
      </Box>
      {/* Children Items */}
      {hasChildren && (
        <Collapse in={isExpanded}>
          {node.children.map((child, childIndex) => (
            <Box
              key={child.permission.roles_code}
              height={'48px'}
              className="row"
              pl={5}
              pr={3}
              py={2}
              sx={{
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.50',
                borderLeft: '4px solid',
                borderLeftColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'primary.100',
                },
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.300', mr: 1.5 }} />
              <Typography variant="body2" color="text.secondary">{child.permission.roles_name}</Typography>
            </Box>
          ))}
        </Collapse>
      )}
    </>
  )
}

// Component สำหรับแสดง checkboxes ของ menu (ขวามือ)
function PermissionMenuCheckboxes({
  node,
  index,
  expandedItems,
  control,
  setValue,
  inheritedPermissions,
}: {
  node: IPermissionNode
  index: number
  expandedItems: Set<string>
  control: Control<{ permissionData: IPermission[] }>
  setValue: UseFormSetValue<{ permissionData: IPermission[] }>
  inheritedPermissions: PermissionInheritanceMap
}) {
  const hasChildren = node.children.length > 0
  const isExpanded = expandedItems.has(node.permission.roles_code)

  return (
    <>
      {/* Root Item Checkboxes */}
      <PermissionField
        originalIndex={node.originalIndex}
        control={control}
        setValue={setValue}
        isRoot={hasChildren}
        childTargets={node.children.map((c) => ({ roles_code: c.permission.roles_code, originalIndex: c.originalIndex }))}
        bgColor={hasChildren ? 'common.white' : (index % 2 === 0 ? 'grey.50' : 'common.white')}
        hasChildren={hasChildren}
        roleCode={node.permission.roles_code}
        inheritedPermissions={inheritedPermissions}
      />
      {/* Children Checkboxes */}
      {hasChildren && (
        <Collapse in={isExpanded}>
          {node.children.map((child, childIndex) => (
            <PermissionField
              key={child.permission.roles_code}
              originalIndex={child.originalIndex}
              control={control}
              setValue={setValue}
              isRoot={false}
              childTargets={[]}
              bgColor="primary.50"
              isChild
              roleCode={child.permission.roles_code}
              inheritedPermissions={inheritedPermissions}
            />
          ))}
        </Collapse>
      )}
    </>
  )
}

// Component สำหรับแสดง checkbox row
function PermissionField({
  originalIndex,
  control,
  setValue,
  isRoot,
  childTargets,
  bgColor,
  isChild = false,
  hasChildren = false,
  roleCode,
  inheritedPermissions,
}: {
  originalIndex: number
  control: Control<{ permissionData: IPermission[] }>
  setValue: UseFormSetValue<{ permissionData: IPermission[] }>
  isRoot: boolean
  childTargets: PermissionTarget[]
  bgColor: string
  isChild?: boolean
  hasChildren?: boolean
  roleCode: string
  inheritedPermissions: PermissionInheritanceMap
}) {
  const inheritedBadge = (action: PermissionAction) => {
    const sources = inheritedPermissions[roleCode]?.[action] || []
    if (!sources.length) return null
    return (
      <Tooltip title={`ได้รับจากกลุ่ม: ${sources.join(', ')}`}>
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label="จากกลุ่ม"
          sx={{ height: 18, fontSize: '0.6rem', ml: 0.25 }}
        />
      </Tooltip>
    )
  }

  const readOnlyDashboard = isExplicitDashboardPermission(roleCode)
  const handleChange = (action: PermissionAction) => (event: ChangeEvent<HTMLInputElement>) => {
    permissionRowChanges(roleCode, originalIndex, isRoot ? childTargets : [], action, event.target.checked)
      .forEach(change => setValue(`permissionData.${change.index}.${change.action}`, change.value))
  }

  return (
    <Stack
      direction={'row'}
      className="row"
      height={'48px'}
      sx={{
        bgcolor: bgColor,
        borderBottom: hasChildren ? '1px solid' : 'none',
        borderBottomColor: 'divider',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: isChild ? 'primary.100' : (hasChildren ? 'grey.50' : 'action.hover'),
        },
        ...(isChild && { borderLeft: '4px solid', borderLeftColor: 'primary.main' }),
      }}
    >
      <Box flex={1} px={1.5} py={0.5} display="flex" alignItems="center">
        <CheckBoxInput
          onChange={handleChange('read')}
          id={`permissionData.${originalIndex}.read`}
          name={`permissionData.${originalIndex}.read`}
          control={control}
        />
        {inheritedBadge('read')}
      </Box>
      <Box flex={1} px={1.5} py={0.5} display="flex" alignItems="center">
        <CheckBoxInput
          onChange={handleChange('create')}
          disabled={readOnlyDashboard}
          id={`permissionData.${originalIndex}.create`}
          name={`permissionData.${originalIndex}.create`}
          control={control}
        />
        {inheritedBadge('create')}
      </Box>
      <Box flex={1} px={1.5} py={0.5} display="flex" alignItems="center">
        <CheckBoxInput
          onChange={handleChange('update')}
          disabled={readOnlyDashboard}
          id={`permissionData.${originalIndex}.update`}
          name={`permissionData.${originalIndex}.update`}
          control={control}
        />
        {inheritedBadge('update')}
      </Box>
      <Box flex={1} px={1.5} py={0.5} display="flex" alignItems="center">
        <CheckBoxInput
          onChange={handleChange('delete')}
          disabled={readOnlyDashboard}
          id={`permissionData.${originalIndex}.delete`}
          name={`permissionData.${originalIndex}.delete`}
          control={control}
        />
        {inheritedBadge('delete')}
      </Box>
      <Box flex={1} px={1.5} py={0.5} display="flex" alignItems="center">
        <CheckBoxInput
          onChange={handleChange('approve')}
          disabled={readOnlyDashboard}
          id={`permissionData.${originalIndex}.approve`}
          name={`permissionData.${originalIndex}.approve`}
          control={control}
        />
        {inheritedBadge('approve')}
      </Box>
    </Stack>
  )
}

// Component สำหรับ Select All header row
function PermissionFieldHeader({
  setValue,
  control,
  getValues,
  replaceFields,
}: {
  control: Control<{ permissionData: IPermission[] }>
  setValue: UseFormSetValue<{ permissionData: IPermission[] }>
  getValues: UseFormGetValues<{ permissionData: IPermission[] }>
  replaceFields?: (value: IPermission[]) => void
}) {
  const watchPermissionFieldValues = useWatch({ control, name: 'permissionData' })

  const isCheckeds = useMemo(() => {
    const permissions = watchPermissionFieldValues || []
    return {
      read: isBulkPermissionChecked(permissions, 'read'),
      update: isBulkPermissionChecked(permissions, 'update'),
      create: isBulkPermissionChecked(permissions, 'create'),
      delete: isBulkPermissionChecked(permissions, 'delete'),
      approve: isBulkPermissionChecked(permissions, 'approve'),
    }
  }, [watchPermissionFieldValues])

  const setSelectAll = (type: 'read' | 'update' | 'create' | 'delete' | 'approve', value: boolean) => {
    bulkPermissionChanges(getValues('permissionData') || [], type, value)
      .forEach(change => setValue(`permissionData.${change.index}.${change.action}`, change.value))
  }

  return (
    <Stack direction={'row'} className="row" height={'48px'} sx={{ 
      bgcolor: 'primary.100',
      borderBottom: '2px solid',
      borderBottomColor: 'primary.main',
    }}>
      <Box flex={1} px={3} py={0.5} display="flex" alignItems="center" justifyContent="flex-start">
        <Checkbox
          size="small"
          checked={isCheckeds.read}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectAll('read', e.target.checked)
          }}
          sx={{ p: 0, ml: 0.5, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
        />
      </Box>
      <Box flex={1} px={3} py={0.5} display="flex" alignItems="center" justifyContent="flex-start">
        <Checkbox
          size="small"
          checked={isCheckeds.create}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectAll('create', e.target.checked)
          }}
          sx={{ p: 0, ml: 0.5, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
        />
      </Box>
      <Box flex={1} px={3} py={0.5} display="flex" alignItems="center" justifyContent="flex-start">
        <Checkbox
          size="small"
          checked={isCheckeds.update}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectAll('update', e.target.checked)
          }}
          sx={{ p: 0, ml: 0.5, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
        />
      </Box>
      <Box flex={1} px={3} py={0.5} display="flex" alignItems="center" justifyContent="flex-start">
        <Checkbox
          size="small"
          checked={isCheckeds.delete}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectAll('delete', e.target.checked)
          }}
          sx={{ p: 0, ml: 0.5, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
        />
      </Box>
      <Box flex={1} px={3} py={0.5} display="flex" alignItems="center" justifyContent="flex-start">
        <Checkbox
          size="small"
          checked={isCheckeds.approve}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectAll('approve', e.target.checked)
          }}
          sx={{ p: 0, ml: 0.5, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
        />
      </Box>
    </Stack>
  )
}
